import * as THREE from 'three/webgpu';
import {
  Fn,
  vec3,
  float,
  smoothstep,
  mix,
  normalWorld,
  positionWorld,
  attribute,
  mx_fractal_noise_vec3
} from 'three/tsl';
import { TERRAIN, heightAt, sampleGround, trackQuery } from './heightfield.js';

export function createTerrainMesh() {
  const size = TERRAIN.size;
  const segments = TERRAIN.grid - 1;
  const geom = new THREE.PlaneGeometry(size, size, segments, segments);
  geom.rotateX(-Math.PI / 2);

  const posAttr = geom.attributes.position;
  const count = posAttr.count;
  const colors = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);

  // Palette definitions
  const C_ROAD = [0.24, 0.22, 0.20];
  const C_DIRT = [0.44, 0.32, 0.20];
  const C_GRASS = [0.24, 0.38, 0.16];
  const C_SAND = [0.65, 0.54, 0.36];
  const C_ROCK_L = [0.46, 0.42, 0.38];
  const C_ROCK_D = [0.28, 0.27, 0.25];

  for (let i = 0; i < count; i++) {
    const x = posAttr.getX(i);
    const z = posAttr.getZ(i);

    const ground = sampleGround(x, z, 1.2);
    const y = ground.height;
    posAttr.setY(i, y);

    const nx = ground.normal.x;
    const ny = ground.normal.y;
    const nz = ground.normal.z;
    normals[i * 3 + 0] = nx;
    normals[i * 3 + 1] = ny;
    normals[i * 3 + 2] = nz;

    const slope = Math.hypot(nx, nz) / Math.max(0.01, ny);
    const query = trackQuery(x, z);
    const d = query.dist;

    let cr, cg, cb;

    // 1. Base biome by altitude and slope
    if (slope > 0.62 || y > 46) {
      // Steep cliffs / High peaks
      const t = Math.min(1.0, (slope - 0.62) / 0.4);
      cr = C_ROCK_L[0] * (1 - t) + C_ROCK_D[0] * t;
      cg = C_ROCK_L[1] * (1 - t) + C_ROCK_D[1] * t;
      cb = C_ROCK_L[2] * (1 - t) + C_ROCK_D[2] * t;
    } else if (y > 30) {
      // Rocky uplands
      const t = (y - 30) / 16;
      cr = C_DIRT[0] * (1 - t) + C_ROCK_L[0] * t;
      cg = C_DIRT[1] * (1 - t) + C_ROCK_L[1] * t;
      cb = C_DIRT[2] * (1 - t) + C_ROCK_L[2] * t;
    } else if (y < 6) {
      // Low sand basin
      const t = Math.max(0, y / 6);
      cr = C_SAND[0] * (1 - t) + C_DIRT[0] * t;
      cg = C_SAND[1] * (1 - t) + C_DIRT[1] * t;
      cb = C_SAND[2] * (1 - t) + C_DIRT[2] * t;
    } else {
      // Plains: grass with dirt patches
      const patch = (Math.sin(x * 0.04) * Math.cos(z * 0.04) + 1) * 0.5;
      cr = C_GRASS[0] * (1 - patch) + C_DIRT[0] * patch;
      cg = C_GRASS[1] * (1 - patch) + C_DIRT[1] * patch;
      cb = C_GRASS[2] * (1 - patch) + C_DIRT[2] * patch;
    }

    // 2. Track corridor blend
    if (d < TERRAIN.outerCorridor) {
      if (d <= TERRAIN.roadHalfWidth) {
        // Roadbed with subtle wheel ruts
        const lat = Math.abs(query.lateral);
        const rut = Math.sin(lat * 0.7) * 0.08;
        cr = C_ROAD[0] - rut;
        cg = C_ROAD[1] - rut;
        cb = C_ROAD[2] - rut;
      } else {
        // Shoulder transition to wild terrain
        const t = (d - TERRAIN.roadHalfWidth) / (TERRAIN.outerCorridor - TERRAIN.roadHalfWidth);
        const st = t * t * (3 - 2 * t);
        const shoulderR = C_ROAD[0] * (1 - st) + C_DIRT[0] * st;
        const shoulderG = C_ROAD[1] * (1 - st) + C_DIRT[1] * st;
        const shoulderB = C_ROAD[2] * (1 - st) + C_DIRT[2] * st;
        cr = shoulderR * (1 - st) + cr * st;
        cg = shoulderG * (1 - st) + cg * st;
        cb = shoulderB * (1 - st) + cb * st;
      }
    }

    colors[i * 3 + 0] = Math.max(0, Math.min(1, cr));
    colors[i * 3 + 1] = Math.max(0, Math.min(1, cg));
    colors[i * 3 + 2] = Math.max(0, Math.min(1, cb));
  }

  geom.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geom.computeBoundingSphere();
  geom.computeBoundingBox();

  // WebGPU/TSL Terrain Material
  const mat = new THREE.MeshStandardNodeMaterial({
    roughness: 0.88,
    metalness: 0.04
  });

  // Color node: blends vertex base colors with procedural fractal noise
  mat.colorNode = Fn(() => {
    const base = attribute('color');
    const noise = mx_fractal_noise_vec3(positionWorld.mul(0.12));
    const tint = noise.mul(0.24).add(0.88);

    // Accentuate steep rock darkening
    const up = vec3(0.0, 1.0, 0.0);
    const slopeFactor = normalWorld.dot(up);
    const rockDarken = smoothstep(float(0.65), float(0.85), slopeFactor);

    return base.mul(tint).mul(mix(float(0.82), float(1.0), rockDarken));
  })();

  // Normal node: procedural micro-relief normal perturbation
  mat.normalNode = Fn(() => {
    const bump = mx_fractal_noise_vec3(positionWorld.mul(0.32)).sub(0.5);
    const perturbed = normalWorld.add(vec3(bump.x.mul(0.12), float(0.0), bump.z.mul(0.12)));
    return perturbed.normalize();
  })();

  const mesh = new THREE.Mesh(geom, mat);
  mesh.receiveShadow = true;
  mesh.castShadow = false;

  return mesh;
}
